import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Ship, Calendar, MessageSquare, FileText, ArrowRight, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/bookings`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setRecentBookings(bookingsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchData();
    }
  }, [token]);

  const statCards = [
    {
      title: 'Croisières actives',
      value: stats?.cruises || 0,
      icon: Ship,
      color: 'bg-[#D4AF37]',
      link: '/admin/cruises',
    },
    {
      title: 'Réservations',
      value: stats?.bookings?.total || 0,
      subtitle: `${stats?.bookings?.pending || 0} en attente`,
      icon: Calendar,
      color: 'bg-[#0A2540]',
      link: '/admin/bookings',
    },
    {
      title: 'Demandes de devis',
      value: stats?.quotes?.total || 0,
      subtitle: `${stats?.quotes?.new || 0} nouvelles`,
      icon: FileText,
      color: 'bg-green-500',
      link: '/admin/quotes',
    },
    {
      title: 'Messages',
      value: stats?.messages?.total || 0,
      subtitle: `${stats?.messages?.new || 0} non lus`,
      icon: MessageSquare,
      color: 'bg-blue-500',
      link: '/admin/messages',
    },
  ];

  return (
    <AdminLayout title="Tableau de bord">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {statCards.map((card, index) => (
            <Link key={index} to={card.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-[#4A5568] truncate">{card.title}</p>
                      <p className="text-xl sm:text-3xl font-display font-bold text-[#0A2540] mt-1">
                        {loading ? '...' : card.value}
                      </p>
                      {card.subtitle && (
                        <p className="text-[10px] sm:text-xs text-[#94A3B8] mt-1">{card.subtitle}</p>
                      )}
                    </div>
                    <div className={`w-9 h-9 sm:w-12 sm:h-12 ${card.color} rounded-lg flex items-center justify-center shrink-0`}>
                      <card.icon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Bookings */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Dernières réservations</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/admin/bookings">
                  Voir tout
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : recentBookings.length > 0 ? (
                <div className="space-y-3">
                  {recentBookings.map((booking) => (
                    <div 
                      key={booking.id} 
                      className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-[#0A2540]">{booking.customer_name}</p>
                        <p className="text-sm text-[#4A5568]">{booking.cruise_name}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          booking.booking_status === 'confirmed' 
                            ? 'bg-green-100 text-green-700'
                            : booking.booking_status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.booking_status === 'confirmed' ? 'Confirmée' : 
                           booking.booking_status === 'cancelled' ? 'Annulée' : 'En attente'}
                        </span>
                        <p className="text-sm text-[#D4AF37] font-semibold mt-1">
                          {booking.total_amount}€
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#94A3B8] py-8">
                  Aucune réservation pour le moment
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/cruises">
                  <Ship className="mr-2 h-4 w-4" />
                  Gérer les croisières
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/bookings">
                  <Calendar className="mr-2 h-4 w-4" />
                  Voir les réservations
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/quotes">
                  <FileText className="mr-2 h-4 w-4" />
                  Traiter les demandes de devis
                </Link>
              </Button>
              <Button asChild className="w-full justify-start" variant="outline">
                <Link to="/admin/messages">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Répondre aux messages
                </Link>
              </Button>
              <hr className="my-4" />
              <Button asChild className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">
                <Link to="/" target="_blank">
                  Voir le site public
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
